export type UserRole = 'staff' | 'staff_plus' | 'admin' | 'admin_plus' | 'super_admin'

export interface AppUser {
  id: string
  email: string
  displayName: string
  role: UserRole
}

export interface Nurse {
  id: string
  nome: string
  cognome: string
  codice_fiscale?: string
  partita_iva?: string
  telefono?: string
  email?: string
  regione: string
  provincia: string
  indirizzo?: string
  residenza?: string
  domicilio?: string
  domicilio_diverso?: boolean
  qualifica?: string
  contratto?: string
  contratto_firmato?: boolean
  contratto_data_firma?: string
  assicurazione?: string
  assicurazione_scadenza?: string
  polizza_massimale?: string
  iban?: string
  opi_provincia?: string
  opi_numero?: string
  data_iscrizione_albo?: string
  data_abilitazione?: string
  pec?: string
  residenza_contratto?: string
  note?: string
  note_history?: any
  step_history?: any
  interview_notes?: string
  data_colloquio?: string
  zone_coperte?: string
  km_spostamento?: string
  tipo_contratto?: string
  tags?: string
  link_c_v?: string
  progetti_attivi?: string[]
  formazione_fv?: boolean
  formazione_qualita?: boolean
  formazione_farmaco?: boolean
  formazione_dispositivo?: boolean
  data_formazione?: string
  source_interview_id?: string
  disponibilita?: string
  fasce_orarie?: string
  created_by?: string
  created_at?: string
  updated_at?: string
  archiviato?: boolean
}

export interface Interview {
  id: string
  nome: string
  cognome: string
  telefono?: string
  email?: string
  qualifica?: string
  regione?: string
  provincia?: string
  indirizzo?: string
  stato: string
  iban?: string
  note?: string
  current_note_input?: string
  note_history?: any
  data_colloquio?: string
  data_follow_up?: string
  tags?: string
  km_spostamento?: string
  zone_coperte?: string
  link_c_v?: string
  tipo_contratto?: string
  codice_fiscale?: string
  partita_iva?: string
  residenza_contratto?: string
  pec_contratto?: string
  data_abilitazione?: string
  albo_iscrizione?: string
  albo_numero?: string
  data_iscrizione_albo?: string
  polizza_numero?: string
  polizza_compagnia?: string
  polizza_massimale?: string
  polizza_scadenza?: string
  step_history?: any
  email_inviata_documenti?: boolean
  punteggio?: number
  created_by?: string
  created_at?: string
  updated_at?: string
  cestino?: boolean
}

export interface Profile {
  id: string
  display_name: string
  email?: string
  role: UserRole
  created_at?: string
}

export interface Patient {
  id: string
  nurse_id?: string
  nome: string
  cognome: string
  telefono?: string
  email?: string
  indirizzo?: string
  centro_ospedaliero?: string
  caregiver_nome?: string
  caregiver_cognome?: string
  caregiver_telefono?: string
  // Case Manager
  case_manager_nome?: string
  case_manager_cognome?: string
  case_manager_telefono?: string
  case_manager_email?: string
  // Specialista
  specialista_nome?: string
  specialista_telefono?: string
  specialista_email?: string
  percorso_tipo: 'naive' | 'switch'
  data_inizio_percorso?: string
  note?: string
  attivo: boolean
  // Dati clinici
  neurologo_prescrittore?: string
  seriale_pompa?: string
  data_dimissione_prevista?: string
  data_impianto_pegj?: string
  data_sostituzione_peg?: string
  dimensioni_tubo?: string
  data_inizio_terapia?: string
  patologia?: string
  dosaggio_f1?: string
  dosaggio_f2?: string
  dosaggio_f3?: string
  dose_mattutina?: string
  dose_extra?: string
  nutrizione_parenterale?: boolean
  discinetico?: boolean
  motivo_abbandono?: string
  data_abbandono?: string
  created_at?: string
}

export interface PatientDiaryEntry {
  id: string
  patient_id: string
  data: string
  titolo: string
  contenuto: string
  tipo: string // 'nota_clinica' | 'cambio_peg' | 'evento' | 'altro'
  created_by?: string
  created_at?: string
}

 

export interface NurseDiaryEntry {
  id: string
  nurse_id: string
  data: string
  titolo: string
  contenuto?: string
  tipo: string
  created_by?: string
  created_at?: string
}

export interface Appointment {
  id: string
  patient_id: string
  nurse_id?: string
  tipo: string
  descrizione: string
  luogo: string
  data_prevista?: string
  ora_prevista?: string
  data_effettiva?: string
  completato: boolean
  ordine: number
  access_id?: string
  note?: string
  cestino?: boolean
  created_at?: string
}

// Italy regions + provinces data
export const regionsData: Record<string, string[]> = {
  'Abruzzo': ['Chieti', "L'Aquila", 'Pescara', 'Teramo'],
  'Basilicata': ['Matera', 'Potenza'],
  'Calabria': ['Catanzaro', 'Cosenza', 'Crotone', 'Reggio Calabria', 'Vibo Valentia'],
  'Campania': ['Avellino', 'Benevento', 'Caserta', 'Napoli', 'Salerno'],
  'Emilia-Romagna': ['Bologna', 'Ferrara', 'Forlì-Cesena', 'Modena', 'Parma', 'Piacenza', 'Ravenna', 'Reggio Emilia', 'Rimini'],
  'Friuli-Venezia Giulia': ['Gorizia', 'Pordenone', 'Trieste', 'Udine'],
  'Lazio': ['Frosinone', 'Latina', 'Rieti', 'Roma', 'Viterbo'],
  'Liguria': ['Genova', 'Imperia', 'La Spezia', 'Savona'],
  'Lombardia': ['Bergamo', 'Brescia', 'Como', 'Cremona', 'Lecco', 'Lodi', 'Mantova', 'Milano', 'Monza e Brianza', 'Pavia', 'Sondrio', 'Varese'],
  'Marche': ['Ancona', 'Ascoli Piceno', 'Fermo', 'Macerata', 'Pesaro e Urbino'],
  'Molise': ['Campobasso', 'Isernia'],
  'Piemonte': ['Alessandria', 'Asti', 'Biella', 'Cuneo', 'Novara', 'Torino', 'Verbano-Cusio-Ossola', 'Vercelli'],
  'Puglia': ['Bari', 'Barletta-Andria-Trani', 'Brindisi', 'Foggia', 'Lecce', 'Taranto'],
  'Sardegna': ['Cagliari', 'Nuoro', 'Oristano', 'Sassari', 'Sud Sardegna'],
  'Sicilia': ['Agrigento', 'Caltanissetta', 'Catania', 'Enna', 'Messina', 'Palermo', 'Ragusa', 'Siracusa', 'Trapani'],
  'Toscana': ['Arezzo', 'Firenze', 'Grosseto', 'Livorno', 'Lucca', 'Massa-Carrara', 'Pisa', 'Pistoia', 'Prato', 'Siena'],
  'Trentino-Alto Adige': ['Bolzano', 'Trento'],
  'Umbria': ['Perugia', 'Terni'],
  "Valle d'Aosta": ['Aosta'],
  'Veneto': ['Belluno', 'Padova', 'Rovigo', 'Treviso', 'Venezia', 'Verona', 'Vicenza'],
}

